import type { FindingKind } from "@shared/ocenaTypy.js";
import type { LensFindingDto } from "../../lib/api.js";

/**
 * Rozbicie tekstu wypowiedzi na fragmenty zwykle i cytowane.
 *
 * Czysta funkcja bez DOM i bez Reacta, zeby dalo sie ja przetestowac wprost -
 * to jedyne miejsce, ktore decyduje, czy wskazanie z metodyki podswietli sie
 * w transkrypcie.
 *
 * ZASADA: podswietlenie pojawia sie WYLACZNIE przy cytacie zweryfikowanym w kodzie
 * (quoteVerified) i o zakresie mieszczacym sie w tekscie. Zakres wychodzacy poza tekst
 * traktujemy dokladnie tak jak niezweryfikowany: podswietlenie musialoby wtedy zgadywac,
 * gdzie konczy sie cytat, a to jest dokladnie ten rodzaj zgadywania, ktoremu weryfikacja
 * cytatu ma zapobiegac.
 *
 * Podswietlenie NIE MA NUMERU. Jedynym numerem w interfejsie jest numer wypowiedzi
 * (shared/numeracja.ts): druga numeracja, chip z liczba przy cytacie, mylila sie z nim. Uwaga przy dymku,
 * ktora trzeba sparowac z podkresleniem, dostaje wiec LITERE (a, b, c), nigdy cyfre. Litere przypisuje
 * policzPodswietlenia (lib/raportOceny.ts), a ten plik tylko niesie id wskazania przy cytacie.
 */

export interface ZakresCytatu {
  /** Id wskazania, do ktorego nalezy cytat: po nim karta oceny sprawdza, czy jej cytat jest w transkrypcie. */
  id: string;
  /** Rodzaj wskazania: od niego zalezy ksztalt podkreslenia (nie sam kolor, A-7). */
  rodzaj: FindingKind;
  start: number;
  koniec: number;
}

export type Fragment =
  | { rodzaj: "tekst"; tekst: string }
  | { rodzaj: "cytat"; tekst: string; rodzajWskazania: FindingKind; id: string };

/**
 * Zakres wskazania, o ile wolno go podswietlic. `null` znaczy "zostaw wskazanie
 * w karcie oceny, nie ruszaj transkryptu".
 */
export function zakresWskazania(
  finding: Pick<LensFindingDto, "quoteVerified" | "quoteStart" | "quoteEnd">,
  dlugoscTekstu: number,
): { start: number; koniec: number } | null {
  if (!finding.quoteVerified) return null;

  const { quoteStart: start, quoteEnd: koniec } = finding;
  if (start === null || koniec === null) return null;
  if (!Number.isInteger(start) || !Number.isInteger(koniec)) return null;
  if (start < 0 || koniec > dlugoscTekstu || start >= koniec) return null;

  return { start, koniec };
}

export interface RozstrzygnieteNakladanie {
  /** Zakresy do narysowania, rosnaco po poczatku, bez nakladania. */
  rysowane: ZakresCytatu[];
  /** Id wskazan, ktorych cytat jest w transkrypcie widoczny (rysowany albo identyczny z rysowanym). */
  widoczne: Set<string>;
  /** Zakresy pominiete, bo nachodza na inny: ich cytatu w transkrypcie nie ma. */
  pominiete: ZakresCytatu[];
}

/**
 * Rozstrzyga nachodzenie cytatow w jednej wypowiedzi. Wygrywa ten, ktory zaczyna sie wczesniej
 * (przy tym samym poczatku - krotszy), bo dwa podkreslenia jeden na drugim daja nieczytelna kase,
 * a nie dwie informacje. Identyczny zakres to ten sam fragment: jest widoczny razem z pierwszym,
 * a nie "pominiety" - karta oceny nie moze mowic, ze cytatu w transkrypcie nie ma, gdy on tam jest.
 *
 * To JEDNO miejsce, ktore o tym decyduje: korzysta z niego i rysowanie fragmentow, i karta oceny.
 */
export function rozstrzygnijNakladanie(zakresy: readonly ZakresCytatu[], dlugoscTekstu: number): RozstrzygnieteNakladanie {
  const posortowane = [...zakresy]
    .filter((z) => z.start >= 0 && z.koniec <= dlugoscTekstu && z.start < z.koniec)
    .sort((a, b) => a.start - b.start || a.koniec - b.koniec);

  const rysowane: ZakresCytatu[] = [];
  const widoczne = new Set<string>();
  const pominiete: ZakresCytatu[] = [];
  let kursor = 0;

  for (const zakres of posortowane) {
    const ostatni = rysowane[rysowane.length - 1];
    if (ostatni && ostatni.start === zakres.start && ostatni.koniec === zakres.koniec) {
      widoczne.add(zakres.id);
      continue;
    }
    if (zakres.start < kursor) {
      pominiete.push(zakres);
      continue;
    }
    rysowane.push(zakres);
    widoczne.add(zakres.id);
    kursor = zakres.koniec;
  }

  return { rysowane, widoczne, pominiete };
}

/** Dzieli tekst na fragmenty zwykle i cytowane. Zakresy nachodzace na siebie: patrz rozstrzygnijNakladanie. */
export function podzielNaFragmenty(tekst: string, zakresy: readonly ZakresCytatu[]): Fragment[] {
  const { rysowane } = rozstrzygnijNakladanie(zakresy, tekst.length);

  const out: Fragment[] = [];
  let kursor = 0;

  for (const zakres of rysowane) {
    if (zakres.start > kursor) out.push({ rodzaj: "tekst", tekst: tekst.slice(kursor, zakres.start) });
    out.push({
      rodzaj: "cytat",
      tekst: tekst.slice(zakres.start, zakres.koniec),
      rodzajWskazania: zakres.rodzaj,
      id: zakres.id,
    });
    kursor = zakres.koniec;
  }

  if (kursor < tekst.length) out.push({ rodzaj: "tekst", tekst: tekst.slice(kursor) });
  return out;
}

/**
 * ZAZNACZENIE TRAFIEN powtarzanego slowa (lista "Powtarzane slowa" -> przycisk "Wypowiedz N"). To nie cytat ze
 * wskazania i nie ocena: nie ma koloru oceny, nie rozstrzyga nakladania z cytatami (lezy NA nich, wewnatrz fragmentu)
 * i znika razem z wyroznieniem dymka. Bez niego przejscie do dlugiej wypowiedzi (setki slow) konczylo sie w srodku
 * sciany tekstu bez sladu, gdzie jest slowo.
 *
 * Zakres pochodzi z liczenia na tekscie po NFC (shared/powtarzaneSlowa.ts). Dla tekstu, ktory nie jest w NFC, pozycje
 * moglyby sie rozjechac z tym, co widac, wiec wtedy nie zaznaczamy niczego (przejscie zostaje, zaznaczenia nie ma).
 */
export interface ZakresZaznaczenia {
  start: number;
  koniec: number;
}

/** Zakresy, ktore wolno zaznaczyc w tym tekscie: w granicach tekstu, rosnaco, bez nakladania. Inaczej pusta lista. */
export function zaznaczeniaWTekscie(
  tekst: string,
  zakresy: readonly ZakresZaznaczenia[] | null | undefined,
): ZakresZaznaczenia[] {
  if (!zakresy || zakresy.length === 0 || tekst.normalize("NFC") !== tekst) return [];
  const poprawne = zakresy
    .filter((z) => Number.isInteger(z.start) && Number.isInteger(z.koniec) && z.start >= 0 && z.koniec <= tekst.length && z.start < z.koniec)
    .map((z) => ({ start: z.start, koniec: z.koniec }))
    .sort((a, b) => a.start - b.start || a.koniec - b.koniec);
  const wynik: ZakresZaznaczenia[] = [];
  for (const z of poprawne) {
    const ostatni = wynik[wynik.length - 1];
    if (!ostatni || z.start >= ostatni.koniec) wynik.push(z);
  }
  return wynik;
}

/** Pozycja poczatku kazdego fragmentu w tekscie wypowiedzi (fragmenty pokrywaja tekst po kolei, bez przerw). */
export function poczatkiFragmentow(fragmenty: readonly Fragment[]): number[] {
  const wynik: number[] = [];
  let pozycja = 0;
  for (const f of fragmenty) {
    wynik.push(pozycja);
    pozycja += f.tekst.length;
  }
  return wynik;
}

/**
 * Tekst jednego fragmentu (zaczynajacego sie na pozycji `od` wypowiedzi) pociety na kawalki zwykle i zaznaczone.
 * Zaznaczenie, ktore wychodzi poza fragment, dostaje tu tylko swoja czesc: reszte zaznacza sasiedni fragment.
 */
export function kawalkiZZaznaczeniem(
  tekst: string,
  od: number,
  zaznaczenia: readonly ZakresZaznaczenia[],
): { tekst: string; zaznaczony: boolean }[] {
  const koniecFragmentu = od + tekst.length;
  const wynik: { tekst: string; zaznaczony: boolean }[] = [];
  let kursor = od;
  for (const z of zaznaczenia) {
    const start = Math.max(z.start, od);
    const koniec = Math.min(z.koniec, koniecFragmentu);
    if (start >= koniec) continue;
    if (start > kursor) wynik.push({ tekst: tekst.slice(kursor - od, start - od), zaznaczony: false });
    wynik.push({ tekst: tekst.slice(start - od, koniec - od), zaznaczony: true });
    kursor = koniec;
  }
  if (kursor < koniecFragmentu) wynik.push({ tekst: tekst.slice(kursor - od), zaznaczony: false });
  return wynik;
}
